import React, { useState } from 'react';
import { useEditor } from '../../../contexts/EditorContext';
import { EDITOR_TEMPLATES, TEMPLATE_CATEGORIES } from '../../../data/editorTemplates';

// CSS-based visual preview for each template
const TemplatePreview = ({ template }) => {
  const { bg, accent } = template.preview;

  const previewStyle = {
    backgroundColor: bg,
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  };

  const bar = (top, left, width, height, color = accent, opacity = 1, radius = 2) => ({
    position: 'absolute',
    top, left, width, height,
    backgroundColor: color,
    opacity,
    borderRadius: radius,
  });

  const previews = {
    blank: (
      <div style={previewStyle}>
        <div style={bar('50%', '50%', 0, 0)} />
        <div style={{ ...bar('45%', '20%', '60%', 2, '#E5E7EB') }} />
        <div style={{ ...bar('55%', '30%', '40%', 2, '#E5E7EB') }} />
      </div>
    ),
    'title-slide': (
      <div style={previewStyle}>
        <div style={bar('38%', '10%', '80%', 10, '#FFFFFF', 0.9, 2)} />
        <div style={bar('55%', '20%', '60%', 6, '#BFDBFE', 0.8, 2)} />
      </div>
    ),
    quote: (
      <div style={previewStyle}>
        <div style={bar('30%', '12%', 4, '40%', accent)} />
        <div style={bar('30%', '22%', '65%', 6, '#374151', 0.7, 2)} />
        <div style={bar('42%', '22%', '60%', 6, '#374151', 0.5, 2)} />
        <div style={bar('54%', '22%', '45%', 6, '#374151', 0.5, 2)} />
        <div style={bar('68%', '22%', '35%', 4, '#9CA3AF', 0.6, 2)} />
      </div>
    ),
    stats: (
      <div style={previewStyle}>
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', fontSize: 28, fontWeight: 'bold', color: accent, lineHeight: 1 }}>
          100%
        </div>
        <div style={bar('62%', '20%', '60%', 6, '#065F46', 0.7, 2)} />
        <div style={bar('72%', '28%', '44%', 4, '#9CA3AF', 0.5, 2)} />
      </div>
    ),
    cta: (
      <div style={previewStyle}>
        <div style={bar('28%', '10%', '80%', 10, '#FFFFFF', 0.9, 2)} />
        <div style={bar('42%', '15%', '70%', 6, '#DDD6FE', 0.7, 2)} />
        <div style={bar('54%', '15%', '65%', 6, '#DDD6FE', 0.5, 2)} />
        <div style={{ ...bar('66%', '20%', '60%', 18, '#FFFFFF', 1, 9), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '50%', height: 4, backgroundColor: accent, borderRadius: 2, opacity: 0.8 }} />
        </div>
      </div>
    ),
    split: (
      <div style={previewStyle}>
        <div style={bar(0, 0, '43%', '100%', accent, 0.15)} />
        <div style={bar('32%', '5%', '33%', 8, accent, 0.8, 2)} />
        <div style={bar('48%', '8%', '27%', 5, accent, 0.5, 2)} />
        <div style={bar('28%', '48%', '45%', 8, '#111827', 0.7, 2)} />
        <div style={{ position: 'absolute', top: '40%', left: '48%', width: 20, height: 3, backgroundColor: accent, borderRadius: 2 }} />
        <div style={bar('46%', '48%', '42%', 5, '#374151', 0.4, 2)} />
        <div style={bar('54%', '48%', '38%', 5, '#374151', 0.4, 2)} />
        <div style={bar('62%', '48%', '35%', 5, '#374151', 0.3, 2)} />
      </div>
    ),
  };

  return previews[template.id] || <div style={previewStyle} />;
};

const TemplatesPanel = () => {
  const { slides, activeSlide, applyTemplate } = useEditor();
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...TEMPLATE_CATEGORIES];

  const filtered = activeCategory === 'All'
    ? EDITOR_TEMPLATES
    : EDITOR_TEMPLATES.filter(t => t.category === activeCategory);

  const handleApply = (template) => {
    const currentElements = slides[activeSlide]?.elements || [];
    const hasContent = currentElements.filter(el => !el.isBackground).length > 0;

    if (hasContent && !window.confirm(`Replace all elements on Slide ${activeSlide + 1} with the "${template.name}" template?`)) {
      return;
    }
    applyTemplate(template);
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-3 bg-gray-50 border-b border-gray-200 shrink-0">
        <h3 className="text-sm font-semibold text-gray-700">Templates</h3>
        <p className="text-xs text-gray-400">{EDITOR_TEMPLATES.length} built-in templates</p>
      </div>

      {/* Category filter */}
      <div className="flex gap-1 p-2 border-b border-gray-200 overflow-x-auto shrink-0">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`shrink-0 px-2 py-1 rounded text-xs font-medium transition-colors ${
              activeCategory === cat
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Template grid */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="grid grid-cols-2 gap-2">
          {filtered.map(template => (
            <div key={template.id} className="group">
              <div
                className="relative w-full rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-blue-400 transition-colors cursor-pointer"
                style={{ paddingBottom: '100%' }}
                onClick={() => handleApply(template)}
                title={`Apply "${template.name}" template`}
              >
                <div className="absolute inset-0">
                  <TemplatePreview template={template} />
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow transition-opacity font-medium">
                    Apply
                  </span>
                </div>
              </div>
              <p className="text-xs text-center mt-1 text-gray-700 font-medium truncate px-1">
                {template.name}
              </p>
              <p className="text-xs text-center text-gray-400 truncate px-1">
                {template.category}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer tip */}
      <div className="p-3 border-t border-gray-200 text-xs text-gray-400 shrink-0">
        💡 Click a template to apply it to the active slide
      </div>
    </div>
  );
};

export default TemplatesPanel;
